import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Activity, Clock, Gauge, Film, RefreshCw, ArrowLeft } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';

const fmtSec = (v) => (v == null ? '—' : `${Number(v).toFixed(2)}s`);
const fmtMB = (bytes) => (bytes == null ? '—' : `${(bytes / 1_048_576).toFixed(1)} MB`);
const fmtDur = (s) => {
  if (s == null) return '—';
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return m > 0 ? `${m}m${String(r).padStart(2, '0')}s` : `${r}s`;
};
const fmtDate = (iso) => (iso ? iso.slice(0, 19).replace('T', ' ') : '—');

const Card = ({ icon: Icon, label, value, sub, testid }) => (
  <div
    data-testid={testid}
    className="p-6 rounded-2xl border border-white/5 bg-white/[0.02]"
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{label}</span>
      <Icon className="w-4 h-4 text-[#D4FF00]" />
    </div>
    <div className="font-heading text-4xl font-black tabular-nums">{value}</div>
    {sub ? <div className="text-xs text-zinc-500 mt-2 font-mono">{sub}</div> : null}
  </div>
);

const StageBar = ({ label, seconds, maxSeconds, sampleCount }) => {
  const width = maxSeconds > 0 ? Math.max(2, (seconds / maxSeconds) * 100) : 0;
  return (
    <div className="mb-3" data-testid={`perf-stage-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-mono text-zinc-300">{label}</span>
        <span className="font-mono text-zinc-500 tabular-nums">
          {fmtSec(seconds)} <span className="text-zinc-600">· n={sampleCount}</span>
        </span>
      </div>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
        <div
          className="h-full bg-[#D4FF00]"
          style={{ width: `${width}%`, transition: 'width 300ms ease-out' }}
        />
      </div>
    </div>
  );
};

const RunRow = ({ run }) => {
  const totalMeasured = Object.values(run.stage_timings || {}).reduce((a, b) => a + (Number(b) || 0), 0);
  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`perf-run-row-${run.id}`}>
      <td className="p-3 font-mono text-xs text-zinc-500">{fmtDate(run.created_at)}</td>
      <td className="p-3">
        <div className="text-sm text-zinc-100">{run.title || '—'}</div>
        <div className="text-[11px] text-zinc-500 font-mono truncate max-w-[240px]">{run.filename}</div>
      </td>
      <td className="p-3 font-mono text-xs text-zinc-400 tabular-nums">{fmtDur(run.duration)}</td>
      <td className="p-3 font-mono text-xs text-zinc-400 tabular-nums">{fmtMB(run.size)}</td>
      <td className="p-3 font-mono text-sm text-zinc-100 tabular-nums font-semibold">{fmtSec(run.processing_time)}</td>
      <td className="p-3 font-mono text-xs text-zinc-500 tabular-nums">{run.clips_count ?? '—'}</td>
      <td className="p-3">
        {run.ai_used ? (
          <span className="text-[10px] px-2 py-0.5 rounded acid-chip">AI</span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded border border-white/10 text-zinc-500">mock</span>
        )}
        {run.ai_error ? (
          <span className="ml-1 text-[10px] px-2 py-0.5 rounded border border-red-500/40 text-red-300">
            {run.ai_error}
          </span>
        ) : null}
      </td>
      <td className="p-3 font-mono text-[10px] text-zinc-500 tabular-nums" title={JSON.stringify(run.stage_timings, null, 2)}>
        Σ {fmtSec(totalMeasured)}
      </td>
    </tr>
  );
};

const PipelinePerformance = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    api.get('/admin/pipeline-performance')
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') load();
  }, [user, load]);

  const sortedStages = useMemo(() => {
    if (!data?.stage_averages) return [];
    return Object.entries(data.stage_averages)
      .map(([label, v]) => ({ label, ...v }))
      .sort((a, b) => b.avg_seconds - a.avg_seconds);
  }, [data]);

  const maxStageSeconds = sortedStages[0]?.avg_seconds || 0;

  if (user && user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="admin-perf-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/admin" className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-mono flex items-center gap-2 hover:text-zinc-200" data-testid="perf-back-link">
            <ArrowLeft className="w-3 h-3" /> Admin
          </Link>
          <h1 className="font-heading text-4xl lg:text-5xl font-black mt-2 tracking-tighter">
            Pipeline Performance
          </h1>
          <p className="text-sm text-zinc-500 mt-1 font-mono">
            Last 20 successfully-processed videos · read-only monitoring
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="perf-refresh-btn"
          className="px-4 py-2 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] disabled:opacity-50 text-xs uppercase tracking-wider font-mono flex items-center gap-2"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error ? (
        <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/5 text-red-300 text-sm mb-6 font-mono" data-testid="perf-error">
          Error: {error}
        </div>
      ) : null}

      {/* Aggregates */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Card
          icon={Film}
          label="Sample size"
          value={data?.aggregates?.count ?? '—'}
          sub="Videos with stage_timings"
          testid="perf-card-count"
        />
        <Card
          icon={Clock}
          label="Avg processing"
          value={fmtSec(data?.aggregates?.avg_processing_time)}
          sub={`min ${fmtSec(data?.aggregates?.min_processing_time)} · max ${fmtSec(data?.aggregates?.max_processing_time)}`}
          testid="perf-card-avg"
        />
        <Card
          icon={Gauge}
          label="Median (p50)"
          value={fmtSec(data?.aggregates?.median_processing_time)}
          testid="perf-card-median"
        />
        <Card
          icon={Activity}
          label="p95"
          value={fmtSec(data?.aggregates?.p95_processing_time)}
          testid="perf-card-p95"
        />
      </div>

      {/* Per-stage averages */}
      <div className="mb-10">
        <h2 className="font-heading text-2xl font-bold mb-2">Average time per stage</h2>
        <p className="text-xs text-zinc-500 mb-5 font-mono">
          Computed across the same 20 runs. Ranked from slowest to fastest.
        </p>
        <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6" data-testid="perf-stage-avg-panel">
          {sortedStages.length === 0 ? (
            <div className="text-sm text-zinc-500 font-mono">
              No stage timings available yet. Process a video with the current pipeline
              to populate this dashboard.
            </div>
          ) : (
            sortedStages.map((s) => (
              <StageBar
                key={s.label}
                label={s.label}
                seconds={s.avg_seconds}
                sampleCount={s.sample_count}
                maxSeconds={maxStageSeconds}
              />
            ))
          )}
        </div>
      </div>

      {/* Runs table */}
      <div>
        <h2 className="font-heading text-2xl font-bold mb-4">Last 20 runs</h2>
        <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden">
          <table className="w-full text-sm" data-testid="perf-runs-table">
            <thead className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/5 font-mono">
              <tr>
                <th className="p-3">When</th>
                <th className="p-3">Video</th>
                <th className="p-3">Duration</th>
                <th className="p-3">Size</th>
                <th className="p-3">Proc. time</th>
                <th className="p-3">Clips</th>
                <th className="p-3">AI</th>
                <th className="p-3">Σ measured</th>
              </tr>
            </thead>
            <tbody>
              {data?.runs?.length ? (
                data.runs.map((run) => <RunRow key={run.id} run={run} />)
              ) : (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-sm text-zinc-500 font-mono">
                    {loading ? 'Loading…' : 'No processed videos yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PipelinePerformance;
